/** Date de facturare Firmă/PF (App-Cumpara / App-Setari). Selectare + adăugare. */
import { useState } from 'react';
import type { BillingProfile } from '../api/types';
import { createBillingProfile } from '../api/endpoints';
import { humanError } from '../api/client';
import { useMutation, useInvalidate, qk } from '../api/hooks';
import { useToast } from '../context/ToastContext';
import { Spinner } from './ui';

export function BillingProfiles({
  profiles,
  selectedId,
  onSelect,
}: {
  profiles: BillingProfile[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [type, setType] = useState<'firma' | 'pf'>('firma');
  const [form, setForm] = useState({ name: '', cui: '', regCom: '', representative: '', address: '', invoiceEmail: '' });
  const invalidate = useInvalidate();
  const toast = useToast();

  const createMut = useMutation({
    mutationFn: () =>
      createBillingProfile({
        type,
        name: form.name,
        cui: type === 'firma' ? form.cui : undefined,
        regCom: type === 'firma' ? form.regCom || undefined : undefined,
        representative: form.representative || undefined,
        address: form.address || undefined,
        invoiceEmail: form.invoiceEmail || undefined,
        isDefault: profiles.length === 0,
      }),
    onSuccess: (p) => {
      invalidate([qk.billing]);
      onSelect(p.id);
      setAdding(false);
      setForm({ name: '', cui: '', regCom: '', representative: '', address: '', invoiceEmail: '' });
      toast.push({ kind: 'ok', title: 'Date de facturare salvate' });
    },
    onError: (err) => toast.push({ kind: 'err', title: 'Nu am putut salva', body: humanError(err) }),
  });

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800 }}>Date de facturare</span>
        {!adding && (
          <button onClick={() => setAdding(true)} style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent-text)' }} data-testid="add-billing">
            + Adaugă
          </button>
        )}
      </div>

      {!adding && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
            <input type="radio" name="billing" checked={selectedId === null} onChange={() => onSelect(null)} style={{ accentColor: 'var(--accent)' }} />
            Factură simplă (persoană fizică, fără date de firmă)
          </label>
          {profiles.map((p) => (
            <label key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="billing" checked={selectedId === p.id} onChange={() => onSelect(p.id)} style={{ accentColor: 'var(--accent)' }} />
              <span style={{ fontWeight: 700 }}>{p.name}</span>
              <span className="pill" style={{ background: 'var(--bg2)', color: 'var(--muted)' }}>{p.type === 'firma' ? 'Firmă' : 'PF'}</span>
            </label>
          ))}
        </div>
      )}

      {adding && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="chip" data-on={type === 'firma'} onClick={() => setType('firma')}>Firmă</button>
            <button className="chip" data-on={type === 'pf'} onClick={() => setType('pf')}>Persoană fizică</button>
          </div>
          <input className="input" placeholder={type === 'firma' ? 'Denumire firmă' : 'Nume complet'} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ padding: '10px 12px' }} />
          {type === 'firma' && (
            <>
              <input className="input" placeholder="CUI (ex. RO12345678)" value={form.cui} onChange={(e) => setForm({ ...form, cui: e.target.value })} style={{ padding: '10px 12px' }} />
              <input className="input" placeholder="Nr. Reg. Com. (opțional)" value={form.regCom} onChange={(e) => setForm({ ...form, regCom: e.target.value })} style={{ padding: '10px 12px' }} />
              <input className="input" placeholder="Reprezentant legal (opțional)" value={form.representative} onChange={(e) => setForm({ ...form, representative: e.target.value })} style={{ padding: '10px 12px' }} />
            </>
          )}
          <input className="input" placeholder="Adresa" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} style={{ padding: '10px 12px' }} />
          <input className="input" type="email" placeholder="Email pentru facturi" value={form.invoiceEmail} onChange={(e) => setForm({ ...form, invoiceEmail: e.target.value })} style={{ padding: '10px 12px' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => createMut.mutate()} disabled={createMut.isPending || form.name.trim().length < 2}>
              {createMut.isPending ? <Spinner /> : 'Salvează datele'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Renunță</button>
          </div>
        </div>
      )}
    </div>
  );
}
